import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

export function isHomeownerOnlyRoute(allowedRoles: readonly string[]): boolean {
  return allowedRoles.length > 0 && allowedRoles.every((r) => r === "customer");
}
import { supabase } from "@/integrations/supabase/client";
import { leaveWithoutGuard } from "@/lib/navigationGuardRegistry";
import { usePortalRole } from "@/hooks/usePortalRole";
import { endCurrentSession } from "@/lib/authNavigation";
import { isDefinitelySignedOut, type AuthErrorLike } from "@/lib/authSessionErrors";
import LoadingScreen from "./LoadingScreen";
import StaffSessionGuard from "./StaffSessionGuard";
import { isProfessionalRoute } from "@/lib/staffSessionPolicy";
import { sessionIdentity } from "@/lib/trustedDevice";

type AllowedRole = "admin" | "consultant" | "installer" | "customer" | "partner" | "marketing_manager" | "introducer" | "introducer_manager" | "sales_manager" | "customer_service" | "surveyor";

interface ProtectedRouteProps {
  allowedRoles: AllowedRole[];
  loginPath: string;
  children: ReactNode;
}

/**
 * Blocks rendering + data-fetch until auth session AND role are resolved.
 *
 * Four outcomes are kept apart, because they need different answers:
 *  - no session / expired session  → back to the right sign-in page;
 *  - the session check itself failed → retry, never a silent sign-out;
 *  - the role lookup failed → retry, never treated as "no access";
 *  - a genuine empty role result → honest access-restricted screen.
 *
 * Access is never granted on an error of any kind.
 */
export default function ProtectedRoute({ allowedRoles, loginPath, children }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, roles, loading, rolesError, sessionError, refreshRoles } = usePortalRole();
  const [sessionCheckError, setSessionCheckError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const mountedRef = useRef(true);
  const attemptRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Invalidate anything still in flight so it cannot navigate later.
      attemptRef.current += 1;
    };
  }, []);

  const returnTo = `?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`;

  const verifySession = useCallback(async () => {
    const attempt = ++attemptRef.current;
    setVerifying(true);
    setSessionCheckError(null);
    try {
      const { data, error } = await supabase.auth.getUser();
      // Ignore answers that arrived after unmount, a newer attempt, or a
      // session that has since hydrated.
      if (!mountedRef.current || attempt !== attemptRef.current) return;
      if (error) {
        // Only a definite missing/expired credential means "sign in again".
        // Anything else (network, 5xx, rate limit) is unknown, so nothing is
        // taken away from a person who may well still be signed in.
        if (!data?.user && isDefinitelySignedOut(error as AuthErrorLike)) {
          leaveWithoutGuard(() => navigate(`${loginPath}${returnTo}`, { replace: true }));
          return;
        }
        setSessionCheckError(error.message || "Your sign-in could not be checked.");
        return;
      }
      if (!data?.user) {
        leaveWithoutGuard(() => navigate(`${loginPath}${returnTo}`, { replace: true }));
      }
      // If a verified user exists, the shared auth listener finishes hydrating roles.
    } catch (caught) {
      if (!mountedRef.current || attempt !== attemptRef.current) return;
      // A rejected call must settle with a retry, never strand a loading screen.
      setSessionCheckError(caught instanceof Error ? caught.message : "Your sign-in could not be checked.");
    } finally {
      if (mountedRef.current && attempt === attemptRef.current) setVerifying(false);
    }
  }, [loginPath, navigate, returnTo]);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      // If the shared session check itself failed, the absence of a session is
      // unknown rather than confirmed: show the retry instead of signing out.
      if (!sessionError) void verifySession();
      return;
    }
    // An authoritative session has arrived: any older verification is stale,
    // and the earlier failure no longer describes this person's state.
    attemptRef.current += 1;
    setVerifying(false);
    setSessionCheckError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, session?.user?.id]);

  // Listen for sign-out while mounted
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!s) leaveWithoutGuard(() => navigate(loginPath, { replace: true }));
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unknownSession = sessionCheckError ?? sessionError;
  if (unknownSession) {
    return (
      <RecoveryScreen
        title="We could not check your sign-in"
        description={`${unknownSession} Please try again in a moment.`}
        loginPath={loginPath}
        busy={verifying}
        onRetry={() => {
          void verifySession();
          void refreshRoles();
        }}
      />
    );
  }

  if (loading) return <LoadingScreen />;

  if (rolesError) {
    return (
      <RecoveryScreen
        title="We could not check your access"
        description={`${rolesError} You are still signed in. Please try again in a moment.`}
        loginPath={loginPath}
        busy={false}
        onRetry={() => void refreshRoles()}
      />
    );
  }

  if (!session) return <LoadingScreen />; // will redirect via effect

  // Homeowner-only areas require a verified email address, read from the
  // server-issued user record. Staff routes keep their existing policy.
  if (isHomeownerOnlyRoute(allowedRoles) && !session.user?.email_confirmed_at) {
    return <Navigate to="/customer/verify-email" replace />;
  }

  // A confirmed empty result is a set-up gap, not a wrong-area denial: these
  // people need somewhere to go, not a dead end.
  if (roles.length === 0) {
    return <NoWorkspaceScreen loginPath={loginPath} />;
  }

  // Check if ANY of the user's roles match the allowed roles
  const hasAccess = roles.some((r) => r && allowedRoles.includes(r as AllowedRole));
  if (!hasAccess) {
    return <UnauthorisedScreen loginPath={loginPath} />;
  }

  // Idle/absolute limits apply only to professional areas for professional
  // accounts; homeowner-only areas never get an admin-style timeout.
  const professionalSession =
    !isHomeownerOnlyRoute(allowedRoles) &&
    isProfessionalRoute(allowedRoles) &&
    roles.some((r) => r && r !== "customer" && allowedRoles.includes(r as AllowedRole));

  return (
    <>
      {professionalSession && (
        <StaffSessionGuard
          userId={session.user.id}
          lastSignInAt={session.user.last_sign_in_at}
          sessionId={sessionIdentity(session)}
          loginPath={loginPath}
        />
      )}
      {children}
    </>
  );
}

function ScreenShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md text-center space-y-6">{children}</div>
    </div>
  );
}

function WarningMark() {
  return (
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mx-auto">
      <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  );
}

function RecoveryScreen({
  title,
  description,
  loginPath,
  busy,
  onRetry,
}: {
  title: string;
  description: string;
  loginPath: string;
  busy: boolean;
  onRetry: () => void;
}) {
  const navigate = useNavigate();
  return (
    <ScreenShell>
      <WarningMark />
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      <p className="text-muted-foreground">{description}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onRetry}
          disabled={busy}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {busy ? "Trying again…" : "Try again"}
        </button>
        <button
          onClick={() => navigate(loginPath, { replace: true })}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-md border border-border text-foreground font-medium hover:bg-muted transition-colors"
        >
          Go to sign in
        </button>
      </div>
    </ScreenShell>
  );
}

/**
 * Signed in, checks all succeeded, but no access has been set up yet.
 * No permission is granted here — only honest routes onwards.
 */
function NoWorkspaceScreen({ loginPath }: { loginPath: string }) {
  const navigate = useNavigate();
  return (
    <ScreenShell>
      <WarningMark />
      <h1 className="text-2xl font-bold text-foreground">Your workspace is not set up yet</h1>
      <p className="text-muted-foreground">
        You are signed in, but no workspace has been assigned to this account yet. If you were invited, please open the link
        in your invitation email. Otherwise please contact the office and we will finish setting up your access.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => navigate("/contact")}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
        >
          Contact the office
        </button>
        <button
          onClick={() => navigate(loginPath, { replace: true })}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-md border border-border text-foreground font-medium hover:bg-muted transition-colors"
        >
          Go to sign in
        </button>
      </div>
    </ScreenShell>
  );
}

function UnauthorisedScreen({ loginPath }: { loginPath: string }) {
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const signInAsSomeoneElse = async () => {
    if (switching) return;
    setSwitching(true);
    setSwitchError(null);
    // The current session must actually end before another account is offered.
    const outcome = await endCurrentSession();
    if (!outcome.ok) {
      setSwitching(false);
      setSwitchError(`${outcome.message ?? "Sign out did not complete."} You are still signed in, so please try again.`);
      return;
    }
    leaveWithoutGuard(() => navigate(loginPath, { replace: true }));
    setSwitching(false);
  };

  return (
    <ScreenShell>
      <WarningMark />
      <h1 className="text-2xl font-bold text-foreground">Access Restricted</h1>
      <p className="text-muted-foreground">
        This account does not have permission to view this area. If you believe this is an error, please contact your administrator.
      </p>
      {switchError && <p className="text-sm text-destructive">{switchError}</p>}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => void signInAsSomeoneElse()}
          disabled={switching}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {switching ? "Signing out…" : "Sign in with a different account"}
        </button>
        <button
          onClick={() => navigate("/", { replace: true })}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-md border border-border text-foreground font-medium hover:bg-muted transition-colors"
        >
          Go to homepage
        </button>
      </div>
    </ScreenShell>
  );
}
